import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  useGetUnreadCountQuery,
  useListNotificationsQuery,
  useMarkNotificationsReadMutation,
} from './notificationsApi';
import { formatDateTime, formatRelativeDays } from '@mmef/shared/lib';
import type { NotificationItem } from '@mmef/shared/api';

/** What each notification type means, in the recipient's terms rather than the system's. */
const DESCRIPTIONS: Record<string, string> = {
  SUBSCRIBER_EVENT_MATCH: 'matches one of your alerts',
  ORGANISER_APPROVED: 'was approved and is now listed',
  ORGANISER_REJECTED: "wasn't approved",
  ORGANISER_EVENT_IMPORTED: 'was picked up from your connected account',
  OPERATOR_NEW_EVENT: 'was submitted for review',
};

/**
 * The bell, with an unread badge and a panel of recent notifications.
 *
 * <p>A panel rather than a page: checking whether anything happened shouldn't cost you your place.
 * Most visits end with "nothing new", and navigating away and back for that is a poor trade.
 *
 * <p>The count polls; the list is only fetched once the panel is opened, so the common case costs
 * one small request a minute rather than fifty rows nobody looked at.
 */
export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const { data: countData } = useGetUnreadCountQuery(undefined, { pollingInterval: 60_000 });
  // `{ skip }` rather than skipToken: this query takes no argument, so there's no argument type to
  // keep honest, and skipToken's `undefined` reads ambiguously against a void-arg query.
  const { data: items, isLoading } = useListNotificationsQuery(undefined, { skip: !open });
  const [markRead] = useMarkNotificationsReadMutation();

  const unread = countData?.unread ?? 0;

  // Opening is what "read" means here — the badge counts what's happened since you last looked.
  useEffect(() => {
    if (open && unread > 0) markRead();
  }, [open, unread, markRead]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus(); // don't strand keyboard focus at the page root
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div className="bell-wrap" ref={containerRef}>
      <button
        ref={buttonRef}
        type="button"
        className={`bell${open ? ' bell--active' : ''}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
        onClick={() => setOpen((wasOpen) => !wasOpen)}
      >
        <span aria-hidden="true">🔔</span>
        {unread > 0 && (
          // aria-hidden because the count is already in the label; announcing it twice is noise.
          <span className="bell__badge" aria-hidden="true">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="bell__panel" role="dialog" aria-label="Notifications">
          <p className="bell__title">Notifications</p>

          {isLoading && <p className="bell__empty">Loading…</p>}

          {items?.length === 0 && (
            <p className="bell__empty">
              Nothing yet. When an event matches one of your alerts, it&apos;ll appear here.
            </p>
          )}

          {items && items.length > 0 && (
            <ul className="bell__list">
              {items.slice(0, 8).map((item) => (
                <BellItem key={item.id} item={item} onNavigate={() => setOpen(false)} />
              ))}
            </ul>
          )}

          <Link className="bell__footer" to="/account/alerts" onClick={() => setOpen(false)}>
            Manage your alerts
          </Link>
        </div>
      )}
    </div>
  );
}

function BellItem({ item, onNavigate }: { item: NotificationItem; onNavigate: () => void }) {
  return (
    <li className={`bell__item${item.read ? '' : ' bell__item--unread'}`}>
      <Link to={`/events/${item.eventId}`} onClick={onNavigate}>
        <span className="bell__item-title">{item.eventTitle}</span>{' '}
        <span className="muted">{DESCRIPTIONS[item.type] ?? 'was updated'}</span>
      </Link>
      <p className="bell__meta">
        <time dateTime={item.sentAt}>{formatDateTime(item.sentAt)}</time>
        {item.eventStartTime && ` · event ${formatRelativeDays(item.eventStartTime).toLowerCase()}`}
      </p>
    </li>
  );
}
